/**
 * Console Disabler Utility
 * 
 * Disables console.log, console.warn, console.error, console.info, and console.debug
 * ONLY in production environment (www.mnymny.com).
 * 
 * Console methods remain active in:
 * - Development (localhost)
 * - Staging (staging.mnymny.com)
 * 
 * This prevents users from seeing debugging information in production
 * while still allowing developers to debug in staging.
 */

/**
 * Check if we're in production (not staging or development)
 * @returns {boolean} True if in production environment
 */
const isProduction = () => {
  if (typeof window === 'undefined') {
    return false; // SSR or non-browser environment
  }
  
  const hostname = window.location.hostname;
  
  // Production is www.mnymny.com (or mnymny.com without staging subdomain)
  // Staging is staging.mnymny.com
  // Development is localhost or 127.0.0.1
  
  const isStaging = hostname.includes('staging.mnymny.com');
  const isDevelopment = hostname.includes('localhost') || hostname.includes('127.0.0.1');
  
  // Only disable in production (not staging, not development)
  return !isStaging && !isDevelopment && (hostname.includes('mnymny.com') || hostname.includes('thewallet.app'));
};

/**
 * Initialize console disabler
 * This should be called as early as possible in the application lifecycle
 */
export const disableConsoleInProduction = () => {
  if (!isProduction()) {
    // Not in production, keep console methods active
    return;
  }
  
  // Store original console methods (in case we need them for debugging)
  const originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug,
    trace: console.trace,
    table: console.table,
    group: console.group,
    groupEnd: console.groupEnd,
    groupCollapsed: console.groupCollapsed,
    time: console.time,
    timeEnd: console.timeEnd,
    count: console.count,
    clear: console.clear
  };
  
  // Override console methods with no-op functions in production
  console.log = () => {};
  console.warn = () => {};
  console.error = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.trace = () => {};
  console.table = () => {};
  console.group = () => {};
  console.groupEnd = () => {};
  console.groupCollapsed = () => {};
  console.time = () => {};
  console.timeEnd = () => {};
  console.count = () => {};
  // Keep console.clear working (useful for users)
  // console.clear = () => {};
  
  // Store original methods on window for emergency debugging (only accessible via browser console)
  if (typeof window !== 'undefined') {
    window.__originalConsole = originalConsole;
    // Add a comment in the console to indicate console is disabled
    // This won't show since we disabled console.log, but it's here for reference
    originalConsole.log('%cConsole disabled in production', 'color: #ff6b6b; font-weight: bold;');
  }
};

/**
 * Re-enable console methods (for emergency debugging)
 * This can be called from browser console: window.__enableConsole()
 */
export const enableConsole = () => {
  if (typeof window !== 'undefined' && window.__originalConsole) {
    console.log = window.__originalConsole.log;
    console.warn = window.__originalConsole.warn;
    console.error = window.__originalConsole.error;
    console.info = window.__originalConsole.info;
    console.debug = window.__originalConsole.debug;
    console.trace = window.__originalConsole.trace;
    console.table = window.__originalConsole.table;
    console.group = window.__originalConsole.group;
    console.groupEnd = window.__originalConsole.groupEnd;
    console.groupCollapsed = window.__originalConsole.groupCollapsed;
    console.time = window.__originalConsole.time;
    console.timeEnd = window.__originalConsole.timeEnd;
    console.count = window.__originalConsole.count;
  }
};

// Make enableConsole available globally for emergency debugging
if (typeof window !== 'undefined') {
  window.__enableConsole = enableConsole;
}

export default disableConsoleInProduction;
